import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Chip,
  Divider,
  Grow,
  CircularProgress,
  Box,
  Grid,
  Paper,
  useTheme,
} from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BadgeIcon from "@mui/icons-material/Badge";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GavelIcon from "@mui/icons-material/Gavel";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PersonIcon from "@mui/icons-material/Person";
import { tokens } from "../../../theme";
import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import { BRAND_BLUE, BRAND_BLUE_LIGHT, formatDate } from "../stageBoardShared";

const GrowTransition = React.forwardRef(function GrowTransition(props, ref) {
  return <Grow ref={ref} {...props} />;
});

const InfoTile = ({ icon: Icon, label, value, isDark }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      borderRadius: "10px",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e4eaf3"}`,
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafd",
      display: "flex",
      alignItems: "flex-start",
      gap: 1.2,
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        bgcolor: `${BRAND_BLUE}18`,
        color: BRAND_BLUE,
      }}
    >
      <Icon fontSize="small" />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word", mt: 0.2, lineHeight: 1.4 }}>
        {value || "—"}
      </Typography>
    </Box>
  </Paper>
);

const LogbookViewerDialog = ({ open, row, onClose, base_url }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [assetDetail, setAssetDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    if (!open || !row) return;
    setAssetDetail(null);
    setFetchError(null);

    const assetId = row.AssetId || row.assetId || row.CollateralAssetId;
    if (!assetId) {
      setAssetDetail(row);
      return;
    }

    setLoading(true);
    fetchJsonDynamic(
      "collateralAssetByColumnValues",
      "asset",
      { id: Number(assetId) },
      base_url,
    )
      .then((res) => {
        if (res?.error) {
          setFetchError("Could not load full asset details.");
          setAssetDetail(row);
          return;
        }
        try {
          const parsed = res?.results ? JSON.parse(res.results) : null;
          const detail = Array.isArray(parsed) ? parsed[0] : parsed;
          let meta = {};
          if (detail?.Metadata) {
            try {
              meta =
                typeof detail.Metadata === "string"
                  ? JSON.parse(detail.Metadata)
                  : detail.Metadata;
              if (typeof meta === "string") meta = JSON.parse(meta);
            } catch {
              meta = {};
            }
          }
          setAssetDetail({ ...(detail || {}), ...(meta || {}) });
        } catch {
          setAssetDetail(row);
        }
      })
      .catch(() => {
        setFetchError("Could not load full asset details.");
        setAssetDetail(row);
      })
      .finally(() => setLoading(false));
  }, [open, row, base_url]);

  if (!row) return null;

  const d = assetDetail || {};
  const registrationNo =
    d.regNumber ||
    d.RegistrationNumber ||
    d.RegNo ||
    d.VehicleRegNo ||
    d.PlateNumber ||
    row.RegistrationNumber ||
    "—";
  const chassisNo =
    d.chassisNumber ||
    d.ChassisNumber ||
    d.ChasisNumber ||
    d.Chassis ||
    row.ChassisNumber ||
    "—";
  const engineNo =
    d.engineNumber ||
    d.EngineNumber ||
    d.Engine ||
    row.EngineNumber ||
    "—";
  const make =
    d.make ||
    d.Make ||
    d.VehicleMake ||
    row.Make ||
    row.AssetTypeName ||
    "—";
  const model =
    d.vehicleModel ||
    d.Model ||
    d.ModelName ||
    d.VehicleModel ||
    row.Model ||
    row.AssetSubTypeName ||
    "—";
  const year =
    d.yearOfManufacture ||
    d.YearOfManufacture ||
    d.ManufactureYear ||
    d.Year ||
    row.Year ||
    "—";
  const color =
    d.colour ||
    d.Color ||
    d.Colour ||
    row.Color ||
    "—";
  const logbookRef =
    d.logbookRef ||
    d.LogbookRef ||
    d.LogbookReference ||
    d.LogbookNo ||
    row.LienReferenceNo ||
    "—";
  const registeredOwner =
    d.registeredOwner ||
    d.RegisteredOwner ||
    d.Owner ||
    row.CustomerName ||
    "—";
  const registeredOn = formatDate(
    row.LienRegisteredOn || d.UpdatedAt || d.CreatedAt,
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" TransitionComponent={GrowTransition} keepMounted={false}>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${BRAND_BLUE_LIGHT} 100%)`,
          color: "#fff",
          pb: 1.5,
        }}
      >
        <MenuBookIcon />
        <Box>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
            Logbook Details
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {row.CustomerName || "Customer"} — Lien #{row.LienId}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
          <Chip label={`Ref: ${row.LienReferenceNo || "—"}`} size="small" color="success" variant="outlined" icon={<GavelIcon />} />
          {row.RegistryName && <Chip label={row.RegistryName} size="small" color="info" variant="outlined" />}
          {row.LienStatus && <Chip label={row.LienStatus} size="small" color="success" />}
        </Stack>

        <Divider sx={{ mb: 2, borderColor: isDark ? "rgba(255,255,255,0.08)" : undefined }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <CircularProgress size={36} sx={{ color: BRAND_BLUE }} />
          </Box>
        ) : (
          <>
            {fetchError && (
              <Typography variant="caption" color="warning.main" display="block" mb={1.5}>
                ⚠ {fetchError} Showing available data.
              </Typography>
            )}

            <Typography variant="overline" color="text.secondary" display="block" mb={1} sx={{ letterSpacing: "0.8px", fontSize: "0.68rem" }}>
              Vehicle / Asset Information
            </Typography>

            <Grid container spacing={1.5} mb={2}>
              <Grid item xs={12} sm={6}>
                <InfoTile icon={ConfirmationNumberIcon} label="Registration No." value={registrationNo} isDark={isDark} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoTile icon={DirectionsCarIcon} label="Make / Model" value={make !== "—" || model !== "—" ? `${make} ${model}`.trim() : "—"} isDark={isDark} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoTile icon={BadgeIcon} label="Chassis No." value={chassisNo} isDark={isDark} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoTile icon={BadgeIcon} label="Engine No." value={engineNo} isDark={isDark} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoTile icon={CalendarTodayIcon} label="Year of Manufacture" value={String(year)} isDark={isDark} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoTile icon={DirectionsCarIcon} label="Color" value={color} isDark={isDark} />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2, borderColor: isDark ? "rgba(255,255,255,0.08)" : undefined }} />

            <Typography variant="overline" color="text.secondary" display="block" mb={1} sx={{ letterSpacing: "0.8px", fontSize: "0.68rem" }}>
              Logbook / Ownership
            </Typography>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <InfoTile icon={MenuBookIcon} label="Logbook Reference" value={logbookRef} isDark={isDark} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoTile icon={PersonIcon} label="Registered Owner" value={registeredOwner} isDark={isDark} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoTile icon={CalendarTodayIcon} label="Lien Registered On" value={registeredOn} isDark={isDark} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoTile icon={GavelIcon} label="Registry" value={row.RegistryName || "—"} isDark={isDark} />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            borderColor: BRAND_BLUE,
            color: BRAND_BLUE,
            "&:hover": { borderColor: BRAND_BLUE_LIGHT, bgcolor: `${BRAND_BLUE}10` },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogbookViewerDialog;
